import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { emptyDraft, publishBatch, serializeQueue, summarize, uploadPublic } from "@/lib/publishQueue";
import { invalidateCatalogCache } from "@/lib/catalogCache";

// Cola persistente en el servidor (P4/D1): carga al abrir, guardado diferido, fotos en segundo plano y "Publicar todo".
export function usePublishQueue({ drafts, setDrafts, game }) {
  const [savedQueue, setSavedQueue] = useState(null); // null = cargando · [] = nada que ofrecer
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ step: "", current: 0, total: 0, sub: "" });
  const lock = useRef(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let alive = true; // StrictMode monta dos veces: una respuesta tardía no debe volver a "restaurar" y bloquear el guardado
    api.get("/seller/publish-draft").then((r) => { if (!alive) return; setSavedAt(r.data?.started_at || r.data?.updated_at || null); setSavedQueue(r.data?.items || []); }).catch(() => { if (alive) setSavedQueue([]); });
    return () => { alive = false; };
  }, []);

  const latest = useRef(null);
  useEffect(() => {
    if (savedQueue === null || savedQueue.length) { return undefined; }
    const body = JSON.stringify({ items: serializeQueue(drafts) });
    latest.current = body;
    const save = () => { if (latest.current === body) { api.put("/seller/publish-draft", JSON.parse(body)).catch(() => {}); latest.current = null; } };
    // Al salir de la página: fetch keepalive (el navegador no la cancela como a XHR).
    const beacon = () => {
      if (latest.current !== body) return;
      latest.current = null;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seller/publish-draft`, { method: "PUT", keepalive: true, body,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("hittcg_token")}` } });
      } catch { /* sin red */ }
    };
    const t = setTimeout(save, 800);
    const hidden = () => { if (document.visibilityState === "hidden") beacon(); };
    window.addEventListener("pagehide", beacon);
    document.addEventListener("visibilitychange", hidden);
    return () => { clearTimeout(t); window.removeEventListener("pagehide", beacon); document.removeEventListener("visibilitychange", hidden); };
  }, [drafts, savedQueue]);
  // Navegación dentro de la app: al desmontar, guarda lo pendiente.
  useEffect(() => () => { if (latest.current) api.put("/seller/publish-draft", JSON.parse(latest.current)).catch(() => {}); }, []);

  const savedAsDrafts = () => (savedQueue || []).map((it) => ({ ...emptyDraft(), ...it, photos: [] }));
  const clearServer = () => api.put("/seller/publish-draft", { items: [] }).catch(() => {});

  const uploadInBackground = (d, onFail) => {
    const files = d.photos.slice();
    if (!files.length) return;
    Promise.allSettled(files.map((f) => uploadPublic(f))).then((res) => {
      const ok = res.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const done = files.filter((_, k) => res[k].status === "fulfilled");
      if (ok.length < files.length) onFail?.(files.length - ok.length);
      setDrafts((ds) => ds.map((x) => (x.id === d.id
        ? { ...x, photos: x.photos.filter((f) => !done.includes(f)), photo_paths: [...(x.photo_paths || []), ...ok] } : x)));
    });
  };

  // Publica `list`; quita de la cola las publicadas y deja las fallidas con su motivo. Bloqueo inmediato ante varios toques.
  const publish = async (list) => {
    if (lock.current || !list.length) return null;
    lock.current = true;
    setLoading(true);
    try {
      const results = await publishBatch(list, game, setProgress);
      const sum = summarize(results);
      if (sum.ok) { try { invalidateCatalogCache(); } catch { /* noop */ } }
      const err = new Map(sum.failed.map((f) => [f.id, f.error]));
      const done = new Set(results.filter((r) => r.ok).map((r) => r.id));
      setDrafts((ds) => {
        const keep = ds.filter((d) => !done.has(d.id)).map((d) => (err.has(d.id) ? { ...d, error: err.get(d.id) } : d));
        return keep.length ? keep : [emptyDraft()];
      });
      return { results, ...sum };
    } finally {
      lock.current = false;
      setLoading(false);
      setProgress({ step: "", current: 0, total: 0, sub: "" });
    }
  };

  return { savedQueue, setSavedQueue, savedAt, savedAsDrafts, clearServer, uploadInBackground, publish, loading, progress, locked: lock };
}
